import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { auth } from './firebase';
import { onAuthStateChanged, updateProfile } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = useCallback(async (firebaseUser) => {
    console.log('Fetching user data for:', firebaseUser?.uid);
    if (firebaseUser) {
      const db = getFirestore();
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User data fetched:', userData);
        setUser({
          ...firebaseUser,
          address: userData.address || '',
          isAdmin: userData.isAdmin || false,
          onboardingCompleted: userData.onboardingCompleted || false,
        });
      } else {
        console.log('User document does not exist');
        setUser({
          ...firebaseUser,
          onboardingCompleted: false,
        });
      }
    } else {
      console.log('No firebase user, setting user to null');
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    console.log('Setting up auth state listener');
    const unsubscribe = onAuthStateChanged(auth, fetchUserData);
    return () => {
      console.log('Cleaning up auth state listener');
      unsubscribe();
    };
  }, [fetchUserData]);

  const updateUserProfile = useCallback(async (updatedData) => {
    console.log('Updating user profile:', updatedData);
    if (user) {
      const db = getFirestore();
      const userDocRef = doc(db, 'users', user.uid);
      
      if (updatedData.displayName) {
        await updateProfile(auth.currentUser, { displayName: updatedData.displayName });
      }

      await setDoc(userDocRef, updatedData, { merge: true });
      await fetchUserData(auth.currentUser);
    }
  }, [user, fetchUserData]);

  const refreshUser = useCallback(async () => {
    console.log('Refreshing user data');
    if (auth.currentUser) {
      await fetchUserData(auth.currentUser);
    }
  }, [fetchUserData]);

  console.log('Current user state:', user);

  const contextValue = useMemo(() => ({
    user,
    loading,
    updateUserProfile,
    refreshUser
  }), [user, loading, updateUserProfile, refreshUser]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContext);
};
